import { Frown, Sparkles } from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import { useI18n } from "@/lib/i18n";

export default function ProblemSolution() {
  const { t } = useI18n();
  const without = t("problem.withoutItems") || [];
  const withItems = t("problem.withItems") || [];

  return (
    <section
      id="why-health-break"
      data-testid="problem-solution-section"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-800 dark:text-emerald-400">
              {t("problem.kicker")}
            </div>
            <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight text-balance text-neutral-900 dark:text-neutral-50">
              {t("problem.h2Pre")}{" "}
              <span className="text-orange-500">{t("problem.h2Highlight")}</span>
            </h2>
            <p className="mt-5 text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed max-w-md">
              {t("problem.lead")}
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Reveal delay={0.05}>
            <Card
              testId="problem-card"
              tone="muted"
              icon={<Frown className="h-5 w-5" />}
              title={t("problem.withoutTitle")}
              items={without}
              dim
            />
          </Reveal>
          <Reveal delay={0.12}>
            <Card
              testId="solution-card"
              tone="emerald"
              icon={<Sparkles className="h-5 w-5" />}
              title={t("problem.withTitle")}
              items={withItems}
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Card({ tone, icon, title, items, dim, testId }) {
  const wrap =
    tone === "emerald"
      ? "border-emerald-800/20 bg-emerald-800 text-stone-50"
      : "border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50";
  const dot =
    tone === "emerald"
      ? "bg-stone-50/15 text-stone-50"
      : "bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300";

  return (
    <div
      data-testid={testId}
      className={`rounded-3xl border p-7 ${wrap} ${dim ? "opacity-95" : ""}`}
    >
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-2xl inline-flex items-center justify-center ${dot}`}>
          {icon}
        </div>
        <div className="font-display font-semibold text-lg">{title}</div>
      </div>

      <ul className="mt-5 space-y-3">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-3 text-sm leading-relaxed">
            <span
              className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                tone === "emerald" ? "bg-orange-300" : "bg-neutral-400"
              }`}
            />
            <span className={tone === "emerald" ? "text-stone-100" : "text-neutral-700 dark:text-neutral-300"}>
              {it}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
